<script lang="ts">
  /**
   * The trip book.
   *
   * Three lanes, because a trip is one of three things and they need completely
   * different treatment: an **idea** with no dates that must not nag, a **plan**
   * with dates that has to reach the calendar, and a **memory** worth keeping so
   * the next planning session starts from what actually happened rather than from
   * nothing.
   *
   * The spec's warning shapes this screen: a planner you must remember to visit is
   * dead by October. So the ideas list is cheap to add to — one field — and the
   * only prompt on the screen is the one that turns an idea into dates.
   *
   * Since F13 the list also carries **what kind of trip each one is**, because the
   * kind is what makes the object page useful and a trip with no kind has no
   * questions, no cost lines and no pack list. An unkinded trip is therefore
   * marked here, in the list, where somebody will see it.
   */
  import { Icon, Panel, zar, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { loadCached } from "../lib/offline.svelte";
  import { driveTime, type Trip, type TripKind } from "../lib/trips";

  let { onopen }: { onopen: (tripId: string) => void } = $props();

  let trips = $state<Trip[]>([]);
  let kinds = $state<TripKind[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let cachedAt = $state<string | null>(null);
  let saving = $state(false);
  let editingId = $state<string | null>(null);
  let quickName = $state("");

  const LANES = [
    { status: "planned" as const, title: "Planned", empty: "Nothing booked. Give an idea dates and it becomes a plan." },
    { status: "idea" as const, title: "Ideas", empty: "No ideas yet. Add anything — it costs nothing to write down." },
    { status: "done" as const, title: "Been there", empty: "Nothing recorded yet. Mark a trip as been there when you get home and the next one of its kind learns from it." },
  ];

  type Draft = {
    name: string; status: Trip["status"]; kind: string; from: string; to: string;
    blurb: string; vibe: string; people: string; budgetZar: string;
    totalCost: string; driveKm: string; driveMinutes: string; rating: string; notes: string;
  };
  const EMPTY: Draft = {
    name: "", status: "idea", kind: "", from: "", to: "", blurb: "", vibe: "",
    people: "", budgetZar: "", totalCost: "", driveKm: "", driveMinutes: "", rating: "", notes: "",
  };
  let draft = $state<Draft>({ ...EMPTY });

  const lane = (s: Trip["status"]) =>
    trips
      .filter((t) => t.status === s)
      // Planned trips sort by when they happen; everything else by most recent.
      .sort((a, b) => (s === "planned" ? (a.from ?? "").localeCompare(b.from ?? "") : (b.from ?? "").localeCompare(a.from ?? "")));

  async function load() {
    loading = true;
    const r = await loadCached<{ trips: Trip[] }>("/trips");
    trips = r.data?.trips ?? [];
    error = r.error;
    cachedAt = r.fresh ? null : r.at;
    loading = false;
  }
  $effect(() => {
    void load();
  });
  $effect(() => {
    void loadCached<{ kinds: TripKind[] }>("/trip-kinds").then((r) => (kinds = r.data?.kinds ?? []));
  });

  /** The whole list in words. Pattern 1 — it is the part a household reads. */
  const sentence = $derived.by(() => {
    if (loading) return "";
    const planned = trips.filter((t) => t.status === "planned").length;
    const unkinded = trips.filter((t) => t.kind === null).length;
    const next = lane("planned").find((t) => t.from && Date.parse(t.from) >= Date.now());
    return (
      `${trips.length} in the book. ` +
      (planned ? `${planned} planned` : "Nothing planned") +
      (next?.from ? `, the next on ${dateZA(next.from)}. ` : ". ") +
      (unkinded
        ? `${unkinded} ${unkinded === 1 ? "does" : "do"} not say what kind of trip ${unkinded === 1 ? "it is" : "they are"} yet.`
        : "Every one knows what kind of trip it is.")
    );
  });

  function startEdit(t: Trip) {
    editingId = t.id;
    draft = {
      name: t.name,
      status: t.status,
      kind: t.kind ?? "",
      // <input type="date"> speaks yyyy-mm-dd only; anything else renders blank
      // and silently discards the value the trip already had.
      from: (t.from ?? "").slice(0, 10),
      to: (t.to ?? "").slice(0, 10),
      blurb: t.blurb ?? "",
      vibe: t.vibe ?? "",
      people: t.people == null ? "" : String(t.people),
      budgetZar: t.budgetZar == null ? "" : String(t.budgetZar),
      totalCost: t.totalCost == null ? "" : String(t.totalCost),
      driveKm: t.driveKm == null ? "" : String(t.driveKm),
      driveMinutes: t.driveMinutes == null ? "" : String(t.driveMinutes),
      rating: t.rating == null ? "" : String(t.rating),
      notes: t.notes ?? "",
    };
  }

  function cancel() {
    editingId = null;
    error = null;
  }

  /** One field, one press. Anything more and ideas stop getting written down. */
  async function quickAdd() {
    if (!quickName.trim()) return;
    saving = true;
    try {
      await api.post("/trips", { name: quickName.trim(), status: "idea" });
      quickName = "";
      await load();
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not add that.";
    } finally {
      saving = false;
    }
  }

  async function save(t: Trip) {
    if (!draft.name.trim()) {
      error = "A name is required.";
      return;
    }
    saving = true;
    error = null;
    try {
      await api.patch(`/trips/${t.id}`, {
        name: draft.name.trim(),
        status: draft.status,
        kind: draft.kind || null,
        from: draft.from || null,
        to: draft.to || null,
        blurb: draft.blurb.trim(),
        vibe: draft.vibe.trim() || null,
        people: draft.people.trim() === "" ? null : Number(draft.people),
        budgetZar: draft.budgetZar.trim() === "" ? null : Number(draft.budgetZar),
        totalCost: draft.totalCost.trim() === "" ? null : Number(draft.totalCost),
        driveKm: draft.driveKm.trim() === "" ? null : Number(draft.driveKm),
        driveMinutes: draft.driveMinutes.trim() === "" ? null : Number(draft.driveMinutes),
        rating: draft.rating.trim() === "" ? null : Number(draft.rating),
        notes: draft.notes.trim() || null,
      });
      cancel();
      await load();
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not save that.";
    } finally {
      saving = false;
    }
  }

  function daysAway(from: string): number {
    return Math.round((Date.parse(from) - Date.now()) / 86_400_000);
  }
</script>

<div class="wrap">
  <section class="card">
    <div class="head">
      <span class="kicker">Somewhere to go</span>
      <span class="sub num">{trips.length} in the book</span>
    </div>
    <p class="sentence">{sentence}</p>
    {#if cachedAt}
      <p class="banner">
        <Icon name="pause" size={13} />
        <span><strong>No signal.</strong> This is the book as it was on {dateZA(cachedAt)}. Adding and editing need signal.</span>
      </p>
    {/if}
    <div class="quick">
      <input
        bind:value={quickName}
        placeholder="Anywhere you have ever said we should go one day…"
        onkeydown={(e) => e.key === "Enter" && quickAdd()}
        aria-label="Add an idea"
      />
      <button class="btn-primary" onclick={quickAdd} disabled={saving || !quickName.trim()}>Add the idea</button>
    </div>
  </section>

  {#each LANES as l (l.status)}
    {@const rows = lane(l.status)}
    <section class="card">
      <div class="head">
        <span class="kicker">{l.title}</span>
        <span class="sub num">{rows.length}</span>
      </div>

      <Panel
        {loading}
        error={error && !editingId ? error : null}
        isEmpty={!loading && rows.length === 0}
        onretry={load}
        rows={2}
      >
        {#snippet empty()}
          {l.empty}
        {/snippet}

        {#each rows as t (t.id)}
          <div class="row">
            <Icon name={t.status === "done" ? "check" : t.status === "planned" ? "calendar" : "waypoint"} size={14} />
            <button class="grow open" onclick={() => onopen(t.id)}>
              <div class="row__t">{t.name}</div>
              {#if t.blurb}<div class="sub">{t.blurb}</div>{/if}
              <div class="chips">
                {#if t.kindLabel}
                  <span class="pill"><Icon name="trip" size={11} /> {t.kindLabel.toLowerCase()}</span>
                {:else}
                  <!-- Amber + glyph + the word. The kind is what makes the rest work. -->
                  <span class="pill pill--warn"><Icon name="alert" size={11} /> kind not set</span>
                {/if}
                {#if t.from}
                  <span class="pill pill--quiet num">
                    {dateZA(t.from)}{#if t.to} → {dateZA(t.to)}{/if}
                  </span>
                {/if}
                {#if t.status === "planned" && t.from}
                  {@const d = daysAway(t.from)}
                  <span class="pill" class:pill--warn={d >= 0 && d <= 30}>
                    {d < 0 ? "under way or past" : d === 0 ? "today" : `in ${d} days`}
                  </span>
                {/if}
                {#if t.nights}<span class="pill pill--quiet">{t.nights} nights</span>{/if}
                {#if t.people}<span class="pill pill--quiet">{t.people} people</span>{/if}
                {#if t.driveKm || t.driveMinutes}
                  <span class="pill pill--quiet num">
                    {t.driveKm ? `${t.driveKm} km` : "—"}{#if t.driveMinutes} · {driveTime(t.driveMinutes)}{/if}
                  </span>
                {/if}
                {#if t.status === "done" && t.totalCost != null}
                  <span class="pill pill--quiet num">spent {zar(t.totalCost)}</span>
                {:else if t.plannedTotal != null}
                  <span class="pill pill--quiet num">
                    {zar(t.plannedTotal)}{#if t.costBlanks}, {t.costBlanks} blank{/if}
                  </span>
                {/if}
                {#if t.unanswered}
                  <span class="pill pill--warn"><Icon name="alert" size={11} /> {t.unanswered} unanswered</span>
                {/if}
                {#if t.rating != null}<span class="pill pill--ok"><Icon name="check" size={11} /> {t.rating}/5</span>{/if}
              </div>
            </button>
            <button class="lnk" onclick={() => (editingId === t.id ? cancel() : startEdit(t))}>
              {editingId === t.id ? "Close" : "Edit"}
            </button>
          </div>

          {#if editingId === t.id}
            <div class="edit">
              <div class="grid">
                <label class="f f--wide"><span>Name</span><input bind:value={draft.name} /></label>
                <label class="f">
                  <span>What kind of trip</span>
                  <select bind:value={draft.kind}>
                    <option value="">Not decided</option>
                    {#each kinds as k (k.id)}
                      <option value={k.id}>{k.label}</option>
                    {/each}
                  </select>
                </label>
                <label class="f">
                  <span>Where it is up to</span>
                  <select bind:value={draft.status}>
                    <option value="idea">An idea</option>
                    <option value="planned">Planned</option>
                    <option value="done">Been there</option>
                  </select>
                </label>
                <label class="f"><span>From</span><input type="date" bind:value={draft.from} /></label>
                <label class="f"><span>To</span><input type="date" bind:value={draft.to} /></label>
                <label class="f"><span>Vibe</span><input bind:value={draft.vibe} placeholder="quiet, bush, no wifi" /></label>
                <label class="f"><span>People</span><input type="number" bind:value={draft.people} /></label>
                <label class="f"><span>Budget (R)</span><input type="number" step="0.01" bind:value={draft.budgetZar} /></label>
                <!-- One way, from Faerie Glen. Both fields are typed, never estimated —
                     the drive time is what F15 bands on and a guessed one would put a
                     four-hour place in the two-hour band. -->
                <label class="f"><span>Distance one way (km)</span><input type="number" bind:value={draft.driveKm} /></label>
                <label class="f"><span>Drive one way (minutes)</span><input type="number" bind:value={draft.driveMinutes} /></label>
                {#if draft.status === "done"}
                  <label class="f"><span>Actually spent (R)</span><input type="number" step="0.01" bind:value={draft.totalCost} /></label>
                  <label class="f"><span>Out of 5</span><input type="number" min="1" max="5" bind:value={draft.rating} /></label>
                {/if}
                <label class="f f--wide">
                  <span>In a line</span>
                  <input bind:value={draft.blurb} placeholder="Why this one, in the words you would use out loud" />
                </label>
                <label class="f f--wide"><span>Notes</span><input bind:value={draft.notes} /></label>
              </div>

              {#if error}
                <p class="err"><Icon name="alert" size={12} /> {error}</p>
              {/if}

              <div class="acts">
                <button class="lnk" onclick={cancel} disabled={saving}>Cancel</button>
                <button class="btn-primary" onclick={() => save(t)} disabled={saving}>
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          {/if}
        {/each}
      </Panel>
    </section>
  {/each}
</div>

<style>
  .wrap { display: flex; flex-direction: column; gap: var(--sp-4); }
  .card {
    padding: var(--sp-4) var(--pad-x) var(--sp-4);
    border: 1px solid var(--line);
    border-radius: var(--r-surface);
    background: var(--s1);
  }
  .head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: var(--sp-2); }
  .kicker { font-size: var(--fs-micro); font-weight: 800; letter-spacing: 0.8px; text-transform: uppercase; color: var(--acc); }
  .sentence { margin: 0 0 var(--sp-3); font-size: var(--fs-body); color: var(--tx2); }
  .quick { display: flex; gap: var(--sp-2); }
  .quick input {
    flex: 1;
    padding: 9px 11px;
    font-size: var(--fs-body);
    color: var(--tx);
    background: var(--bg);
    border: 1px solid var(--line);
    border-radius: var(--r-control);
  }
  .banner {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    margin: 0 0 var(--sp-3);
    padding: var(--sp-2) var(--sp-3);
    border-left: 2px solid var(--mut);
    border-radius: var(--r-control);
    background: var(--fill);
    font-size: var(--fs-small);
    color: var(--tx2);
  }
  .row {
    display: flex;
    align-items: flex-start;
    gap: var(--sp-2);
    /* Density from the surface, never a literal. */
    min-height: var(--row-h);
    padding: var(--sp-3);
    border: 1px solid var(--line);
    border-radius: var(--r-control);
    margin-bottom: 6px;
  }
  .grow { flex: 1; min-width: 0; }
  .open { text-align: left; }
  .open:hover .row__t { color: var(--acc); }
  .row__t { font-size: var(--fs-body); font-weight: 700; color: var(--tx); }
  .chips { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 6px; }
  .pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 7px;
    font-size: var(--fs-micro);
    font-weight: 700;
    border-radius: var(--r-pill);
    background: var(--s2);
    border: 1px solid var(--line);
    color: var(--tx);
  }
  .pill--quiet { color: var(--mut); }
  /* Amber for anything wanting attention, never green-vs-red, and the words say it too. */
  .pill--warn { color: var(--warn); border-color: var(--warn); }
  .pill--ok { color: var(--ok); }
  .num { font-variant-numeric: tabular-nums; }
  .edit {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
    margin: 0 0 var(--sp-3);
    padding: var(--sp-4);
    border: 1px solid var(--line);
    border-radius: var(--r-surface);
    background: var(--s2);
  }
  .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: var(--sp-3); }
  .f { display: flex; flex-direction: column; gap: 4px; font-size: var(--fs-micro); font-weight: 700; color: var(--mut); }
  .f--wide { grid-column: 1 / -1; }
  .f input, .f select {
    min-height: 38px;
    padding: 8px 10px;
    font-size: var(--fs-body);
    font-weight: 500;
    color: var(--tx);
    background: var(--bg);
    border: 1px solid var(--line);
    border-radius: var(--r-control);
  }
  .acts { display: flex; justify-content: flex-end; align-items: center; gap: var(--sp-3); margin-top: 4px; }
  .lnk { font-size: var(--fs-micro); font-weight: 700; color: var(--mut); }
  .lnk:hover { color: var(--acc); }
  .err { display: inline-flex; align-items: center; gap: 5px; margin: 0; font-size: var(--fs-small); font-weight: 700; color: var(--warn); }
  .sub { font-size: var(--fs-small); color: var(--mut); }
</style>
